import { IBlogPostWidgetData, IPostWidgetLiveRenderingData, IPostWidgetStyle } from "@pageBuilder/typings";
import { Base64Util } from "../../../utils/base64-util";
import { BlogPostScriptRenderer } from "./blog-post-script-renderer";
import { BlogPostStylesRenderer } from "./blog-post-style-renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { IRenderingCommonContext } from "@commonRendering/types";

export class BlogPostWidgetRenderer extends WidgetRendererBase<IBlogPostWidgetData> {

    private readonly RENDERING_DATA_ATTRIBUTE = "data-blog-post";
    private readonly MODEL_DATA_ATTRIBUTE = "data-post-model";

    private readonly scriptRenderer: BlogPostScriptRenderer;
    private readonly styleRenderer: BlogPostStylesRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new BlogPostScriptRenderer();
        this.styleRenderer = new BlogPostStylesRenderer(this.styleService);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("blogPostWidget");
    }

    protected renderInternal(element: HTMLElement, data: IBlogPostWidgetData): HTMLElement {
        this.setAttributes(element, data);

        this.renderStyles(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        return element;
    }

    protected renderStyles(element: HTMLElement, data: IBlogPostWidgetData): void {
        this.renderBaseStyles(element, data.style);
    }

    protected renderBaseStyles(element: HTMLElement, style: IPostWidgetStyle): void {
        this.styleRenderer.render(element, style);
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    private setAttributes(element: HTMLElement, data: IBlogPostWidgetData): void {
        const liveRenderingData = this.createLiveRenderingData(data);
        const json = Base64Util.serialize(liveRenderingData) || "";

        element.setAttribute(this.RENDERING_DATA_ATTRIBUTE, json);
        if (data.postDataBase64) {
            element.setAttribute(this.MODEL_DATA_ATTRIBUTE, data.postDataBase64);
        }

        element.toggleAttribute("data-primary-element", data.primaryElement);
        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }
    }

    private createLiveRenderingData(data: IBlogPostWidgetData): IPostWidgetLiveRenderingData {
        return {
            showAuthor: data.showAuthor ?? false,
            showPublishDate: data.showPublishDate ?? false,
            showTitle: data.showTitle ?? false,
            showCategories: data.showCategories ?? false,
            showTags: data.showTags ?? false
        };
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [
            ...this.styleRenderer.getStyleSelectors(element)
        ];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

}
